///
/// EarthBrowser
///
/// Created by Matt Giger on 9/15/2024
/// Copyright (c) 2024 Geodata Labs
///

import { EarthTileGeometry, meshResolutionFor } from "@/earthbrowser/activities/EarthActivity/tilecache/EarthTileGeometry"
import { EarthTileMaterial } from "@/earthbrowser/activities/EarthActivity/tilecache/EarthTileMaterial"
import { type TileId } from "@/earthbrowser/activities/EarthActivity/tilecache/TileId"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { geographic } from "@/earthbrowser/utils/geographic"
import { type TileElevationMetadata } from "@/earthbrowser/utils/loaders/ElevationLoader"
import { isMemoryConstrainedDevice } from "@/earthbrowser/utils/MemoryLog"
import { type Renderer } from "@stovokor/engine/Renderer"
import { type vec3 } from "@stovokor/math/vector"
import { type Texture } from "@stovokor/shader/Texture"

export interface TileInterface {
	destroy(): void
	readyToBeParent: boolean
}

export class EarthTile implements TileInterface {
	public tileId: TileId
	public wanted: boolean = false
	public centerDistance: number = 0
	public lastAccess: number = 0

	public geometry: EarthTileGeometry
	public material: EarthTileMaterial

	public sectionsToRender: number = 0b1111
	public renderer?: Renderer

	private context: EarthBrowserContext
	private _center?: vec3

	constructor(context: EarthBrowserContext, tileId: TileId, globalTexture?: Texture, parentMaterial?: EarthTileMaterial) {
		this.context = context
		this.tileId = tileId
		this.geometry = new EarthTileGeometry(context, tileId, 4)
		this.material = new EarthTileMaterial(context, tileId, globalTexture, parentMaterial)
		if(!isMemoryConstrainedDevice()) {
			this.material.onElevationLoaded = metadata => this.refineMesh(metadata)
		}
	}

	public destroy(): void {
		this.material.onElevationLoaded = undefined
		this.material.destroy()
		this.geometry.destroy()
	}

	/** World position of the tile center on the surface, for depth sorting */
	public get center(): vec3 {
		if(!this._center) {
			const origin = this.tileId.origin
			const delta = this.tileId.delta
			this._center = new geographic(origin.x + delta.x / 2, origin.y + delta.y / 2, 0).cartesian
		}
		return this._center
	}

	/**
	 * Swap in the fine mesh once the elevation metadata shows mountainous
	 * relief. The renderer bound the old vertex buffers, so it is rebuilt on
	 * the next frame (the same way overlay changes reset it).
	 */
	private refineMesh(metadata: TileElevationMetadata | undefined): void {
		const resolution = meshResolutionFor(this.tileId, metadata)
		if(resolution !== this.geometry.resolution) {
			this.geometry.destroy()
			this.geometry = new EarthTileGeometry(this.context, this.tileId, 4, resolution)
			this.renderer = undefined
		}
	}

	public get readyToBeParent(): boolean {
		return this.material.readyToBeAParent
	}

	/**
	 * If the child tile is loaded and fully transitioned, don't render the parent's section
	 *
	 * @param entries the tiles actually being rendered this frame - a loaded child that
	 * isn't in the rendered set must not punch a hole in its parent
	 */
	public calculatePatches(entries: Map<string, EarthTile>): void {
		this.sectionsToRender = 0b1111
		const children = this.tileId.children
		for(let i = 0; i < 4; i++) {
			const tile = entries.get(children[i].key)
			// Only hide parent section when child is fully visible:
			// - texture is loaded
			// - texture fade is complete (textureFade === 0)
			if(tile?.material.textureLoaded && tile.material.textureFade === 0) {
				this.sectionsToRender &= ~(1 << i)
			}
		}
	}
}